import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'
import { type OutputParametricSelector, type OutputSelector } from 'reselect'

import { TabsStyled } from './styles/TabsStyled'
import { Tab } from './Tab'
import { TabLink } from './TabLink'

export interface GenericCount {
  [key: string]: number
}

export interface ITabsProps<T = any> {
  countsSelector?: () =>
    | OutputSelector<any, T, (res: any) => T>
    | OutputParametricSelector<any, any, T, (res: any) => T>
  counts?: T
  children?: ((counts: T) => ReactNode) | ReactNode
  /**
   * Changes the colors of the count badge on each to be "incomplete"/"inactive"
   */
  isFiltered?: boolean
}

interface TabsComponent {
  <T = GenericCount>(props: ITabsProps<T>): JSX.Element
  Tab: typeof Tab
  TabLink: typeof TabLink
}

const TabsBase = <T = GenericCount,>(props: ITabsProps<T>) => {
  const { children, countsSelector, counts: countsProp, ...otherProps } = props

  const selectorFn = useMemo(
    () =>
      countsSelector
        ? (countsSelector() as unknown as (state: any) => T)
        : undefined,
    [countsSelector]
  )

  const countsFromStore = useSelector((state: any) =>
    selectorFn ? selectorFn(state) : undefined
  )

  const counts = countsFromStore ?? countsProp

  if (children && isFunction(children)) {
    const renderChildren = children as (counts: T) => ReactNode
    return <TabsStyled {...otherProps}>{renderChildren(counts)}</TabsStyled>
  }

  return <TabsStyled {...props} />
}

export const Tabs: TabsComponent = Object.assign(TabsBase, {
  Tab,
  TabLink,
})
